import { getErrorCorrelationId } from "@/config/correlation-id";
import i18next from "i18next";
import type { ReactNode } from "react";
import { toast } from "sonner";

interface ShowApiErrorToastOptions {
  description?: string;
}

/**
 * Shows an error toast for a failed API request. When the request has a correlation ID, the toast shows its short form
 * with a copy action, so a screenshot or support ticket leads straight to the request in Loki and Sentry. Use plain
 * `toast.error` for errors that do not come from an API request.
 */
export function showApiErrorToast(message: string, error: unknown, { description }: ShowApiErrorToastOptions = {}) {
  const correlationId = getErrorCorrelationId(error);

  if (!correlationId) {
    if (description) {
      toast.error(message, { description });
    } else {
      toast.error(message);
    }
    return;
  }

  const errorId = i18next.t("common:ErrorId", { id: correlationId.slice(0, 8) });

  toast.error(message, {
    description: description ? withErrorId(description, errorId) : errorId,
    action: {
      label: i18next.t("common:Copy"),
      onClick: () => void copyCorrelationId(correlationId),
    },
  });
}

function withErrorId(description: string, errorId: string): ReactNode {
  return (
    <>
      <span className="block">{description}</span>
      <span className="block">{errorId}</span>
    </>
  );
}

async function copyCorrelationId(correlationId: string) {
  try {
    await navigator.clipboard.writeText(correlationId);
    toast.success(i18next.t("common:ErrorIdCopied"));
  } catch {
    toast.error(i18next.t("common:ErrorCopyingToClipboard"));
  }
}
